import {JOB_STATUS_SEVERITY, PrintStoryDoc, PrintStoryEvent, PrintStorySeverity} from '../types'
import {toMillis} from '../time'

const isPrefetchJob = (job: PrintStoryDoc): boolean =>
  job?.jobKind === 'prefetch' ||
  job?.type === 'prefetch' ||
  String(job?.jobId || job?.id || '').startsWith('prepare:')

const jobLane = (job: PrintStoryDoc): 'prefetch' | 'print' =>
  isPrefetchJob(job) ? 'prefetch' : 'print'

const jobSeverity = (status: string): PrintStorySeverity =>
  JOB_STATUS_SEVERITY[status] || 'neutral'

const getJobTs = (job: PrintStoryDoc, ...fields: any[]): number | undefined => {
  for (const field of fields) {
    const ms = toMillis(field)
    if (ms) {
      return ms
    }
  }
  return undefined
}

export const mapPrintJobToEvents = (job: PrintStoryDoc): PrintStoryEvent[] => {
  const events: PrintStoryEvent[] = []
  const lane = jobLane(job)
  const laneKey = lane === 'prefetch' ? String(job.deviceId || job.jobId || job.id) : String(job.jobId || job.id)
  const status = String(job.status || '').toLowerCase()
  const deliveryStatus = String(job.deliveryStatus || '').toLowerCase()
  const jobId = String(job.jobId || job.id)
  const base = {
    lane,
    laneKey,
    deviceId: job.deviceId ? String(job.deviceId) : undefined,
    jobId,
    contentId: job.contentId ? String(job.contentId) : undefined,
    codeId: job.codeId ? String(job.codeId) : undefined,
    sourceCollection: 'printJobs',
    raw: job,
  }

  const createdAt = getJobTs(
    job,
    job.timestamps?.createdAt,
    job.createdAtMs,
    job.createdAt,
    job.publishedAtMs
  )
  if (createdAt) {
    events.push({
      ...base,
      ts: createdAt,
      kind: 'job_created',
      severity: 'info',
      title: `${lane === 'prefetch' ? 'Prefetch' : 'Print'} job created`,
      detail: job.source ? `Source: ${job.source}` : undefined,
    })
  }

  const publishedAt = getJobTs(job, job.publishedAtMs, job.timestamps?.publishedAt, job.publishedAt)
  if (publishedAt && deliveryStatus !== 'not_published_printer_unavailable') {
    events.push({
      ...base,
      ts: publishedAt,
      kind: 'job_published',
      severity: 'warning',
      title: `${lane === 'prefetch' ? 'Prefetch' : 'Print'} published`,
      detail: job.deviceId ? `Device ${job.deviceId}` : undefined,
    })
  }

  if (status === 'blocked' || deliveryStatus === 'not_published_printer_unavailable') {
    const blockedAt = getJobTs(
      job,
      job.timestamps?.blockedAt,
      job.blockedAt,
      job.publishedAtMs,
      createdAt
    )
    if (blockedAt) {
      events.push({
        ...base,
        ts: blockedAt,
        kind: 'job_blocked',
        severity: 'danger',
        title: `${lane === 'prefetch' ? 'Prefetch' : 'Print'} blocked`,
        detail: job.staffMessage || job.reason || 'Printer unavailable at publish',
      })
    }
  }

  if (deliveryStatus === 'mqtt_publish_failed' || status === 'failed' && !job.callbackReceived) {
    const failedAt = getJobTs(
      job,
      job.timestamps?.failedAt,
      job.failedAt,
      job.updatedAtMs,
      job.updatedAt,
      publishedAt,
      createdAt
    )
    if (failedAt && deliveryStatus === 'mqtt_publish_failed') {
      events.push({
        ...base,
        ts: failedAt,
        kind: 'job_publish_failed',
        severity: 'danger',
        title: 'MQTT publish failed',
        detail: job.lastError || job.staffMessage || job.reason,
      })
    }
  }

  if (status === 'completed') {
    const completedAt = getJobTs(
      job,
      job.timestamps?.completedAt,
      job.completedAt,
      job.lastCallbackAtMs,
      job.mqttCallbackLastReceivedAt,
      job.updatedAtMs,
      job.updatedAt
    )
    if (completedAt) {
      events.push({
        ...base,
        ts: completedAt,
        endTs: completedAt,
        kind: 'job_completed',
        severity: 'success',
        title: `${lane === 'prefetch' ? 'Prefetch' : 'Print'} completed`,
        detail:
          job.confirmedCopies != null
            ? `Confirmed copies: ${job.confirmedCopies}`
            : job.staffMessage,
      })
    }
  }

  if (status === 'failed') {
    const failedAt = getJobTs(
      job,
      job.timestamps?.failedAt,
      job.failedAt,
      job.lastCallbackAtMs,
      job.updatedAtMs,
      job.updatedAt,
      publishedAt
    )
    if (failedAt) {
      events.push({
        ...base,
        ts: failedAt,
        kind: 'job_failed',
        severity: 'danger',
        title: `${lane === 'prefetch' ? 'Prefetch' : 'Print'} failed`,
        detail: job.lastError || job.staffMessage || job.reason || job.errorCode,
      })
    }
  }

  if (
    status === 'callback_missing' ||
    deliveryStatus.includes('callback_missing') ||
    job.callbackMissing === true
  ) {
    const missingAt = getJobTs(
      job,
      job.callbackMissingAtMs,
      job.timestamps?.callbackMissingAt,
      job.callbackDueAtMs,
      job.updatedAtMs
    )
    if (missingAt) {
      events.push({
        ...base,
        ts: missingAt,
        kind: 'job_callback_missing',
        severity: 'danger',
        title: 'Callback missing',
        detail: job.staffMessage || `Due at ${job.callbackDueAtMs || 'unknown'}`,
      })
    }
  }

  if (job.callbackReceived === true || job.mqttCallback || job.lastCallbackAtMs) {
    const callbackAt = getJobTs(
      job,
      job.lastCallbackAtMs,
      job.mqttCallbackLastReceivedAt,
      job.mqttCallback?.receivedAt,
      job.mqttCallback?.timestamp
    )
    if (callbackAt) {
      events.push({
        ...base,
        lane: 'callback',
        ts: callbackAt,
        kind: 'callback_received',
        severity: jobSeverity(status === 'failed' ? 'failed' : 'completed'),
        title: 'Callback received',
        detail: job.mqttCallback?.result || job.mqttCallback?.stage,
      })
    }
  }

  return events
}

export const mapPrintJobsToEvents = (jobs: PrintStoryDoc[]): PrintStoryEvent[] =>
  jobs.flatMap(mapPrintJobToEvents)

export const isPrefetchPrintJob = isPrefetchJob

export const isPhysicalPrintJob = (job: PrintStoryDoc): boolean =>
  job?.type === 'print' ||
  job?.jobKind === 'print' ||
  String(job?.jobId || job?.id || '').startsWith('print:')

/** Publish→callback span for Gantt-style lane bars */
export const getJobSpan = (
  job: PrintStoryDoc
): {startMs: number; endMs: number; status: string} | null => {
  const startMs =
    toMillis(job.publishedAtMs) ||
    toMillis(job.timestamps?.publishedAt) ||
    toMillis(job.timestamps?.createdAt) ||
    toMillis(job.createdAtMs) ||
    toMillis(job.createdAt)

  if (!startMs) {
    return null
  }

  const endMs =
    toMillis(job.timestamps?.completedAt) ||
    toMillis(job.timestamps?.failedAt) ||
    toMillis(job.lastCallbackAtMs) ||
    toMillis(job.callbackMissingAtMs) ||
    toMillis(job.timestamps?.callbackMissingAt) ||
    toMillis(job.updatedAtMs) ||
    toMillis(job.updatedAt) ||
    (['published', 'pending'].includes(String(job.status || '').toLowerCase())
      ? Date.now()
      : startMs)

  return {
    startMs,
    endMs: Math.max(endMs, startMs),
    status: String(job.status || 'pending').toLowerCase(),
  }
}
